/**
 * Raster aus Kacheln in Spalten (D-07, BE-06) - fuer die Uebersicht und jede
 * Bereichsseite. Im Anpassen-Modus tragen die Kacheln eine Leiste zum
 * Verschieben und Entfernen; "+ Kachel hinzufuegen" oeffnet den Katalog.
 */
import { useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Plus, RotateCcw, X } from 'lucide-react';

import { PageColumn, PageGrid } from '@/ui/composites/Page';
import { Button } from '@/ui/primitives/Button';
import { Input } from '@/ui/primitives/Input';
import { statistikAnlegen, statistikLoeschen, zaehlerAnlegen, zaehlerLoeschen } from '@/db/repositories/oberflaeche';
import { DARSTELLUNGEN, QUELLEN, type StatistikDarstellung, type StatistikQuelle } from '@/domain/oberflaeche/statistik';
import { StatistikEigeneKachel } from '@/modules/kacheln/StatistikEigeneKachel';
import { ZaehlerKachel } from '@/modules/kacheln/ZaehlerKachel';
import { Dialog } from '@/ui/primitives/Dialog';
import { KACHEL_KOMPONENTEN } from '@/modules/kacheln/katalog';
import { useKachelLayout } from '@/modules/kacheln/useKachelLayout';
import { useSpalten } from '@/modules/kacheln/useSpalten';
import { entferne, fuegeHinzu, fuegePlatzHinzu, hinzufuegbar, KACHEL_TYPEN, layoutIstLeer, spaltenFuer, verschiebe, type Richtung } from '@/domain/oberflaeche/kacheln';
import type { FesterBereich } from '@/domain/types';
import { cn } from '@/lib/utils/cn';

const knopf = 'grid size-7 shrink-0 place-items-center rounded text-ink-faint hover:bg-surface hover:text-ink disabled:opacity-30';

export function KachelRaster({ layoutId, bearbeiten, fest, areaId }: { layoutId: string; bearbeiten: boolean; fest?: FesterBereich; /** Nur auf Bereichsseiten: dort lassen sich eigene Zähler anlegen. */ areaId?: string }) {
  const { layout, aendern, zuruecksetzen } = useKachelLayout(layoutId, fest);
  const n = useSpalten();
  const [katalogOffen, setKatalogOffen] = useState(false);
  const [zName, setZName] = useState('');
  const [zZiel, setZZiel] = useState('');
  const [sQuelle, setSQuelle] = useState<StatistikQuelle>('erledigt');
  const [sDarst, setSDarst] = useState<StatistikDarstellung>('saeulen');
  const fluss = n < 3;
  const spalten = spaltenFuer(layout, n);
  const frei = hinzufuegbar(layout, fest);
  const flach = spalten.flat();

  return (
    <>
      {bearbeiten && (
        <div role="region" aria-label="Anpassen" className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-line-strong px-4 py-3 text-sm text-ink-muted">
          <span className="min-w-full flex-1 sm:min-w-0">Kacheln verschieben, entfernen oder hinzufügen. Die Anordnung gilt auf allen deinen Geräten.</span>
          <Button size="sm" onClick={() => setKatalogOffen(true)} disabled={frei.length === 0 && !areaId}><Plus className="size-4" /> Kachel hinzufügen</Button>
          <Button size="sm" variant="ghost" onClick={() => void zuruecksetzen()}><RotateCcw className="size-4" /> Auf Standard zurücksetzen</Button>
        </div>
      )}

      {layoutIstLeer(layout) && !bearbeiten && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-line-strong px-4 py-12 text-center">
          <p className="text-[15px] font-bold">Diese Seite ist noch leer</p>
          <p className="max-w-md text-sm text-ink-muted">Stell sie dir selbst zusammen: Aus dem Katalog kommen Aufgaben, Gewohnheiten, Ziele, Termine, Notizen und mehr – jeweils nur für diesen Bereich.</p>
          <Button variant="primary" onClick={() => setKatalogOffen(true)}><Plus className="size-4" /> Kachel hinzufügen</Button>
        </div>
      )}

      <PageGrid layout="drei">
        {spalten.map((spalte, s) => (
          <PageColumn key={s}>
            {spalte.map((platz) => {
              const istZaehler = platz.typ === 'zaehler';
              const istStatistik = platz.typ === 'statistik';
              const Komponente = istZaehler || istStatistik ? null : KACHEL_KOMPONENTEN[platz.typ];
              if (!istZaehler && !istStatistik && !Komponente) return null; // Typ aus einer neueren Version
              const titel = istZaehler ? 'Zähler' : istStatistik ? 'Statistik' : KACHEL_TYPEN.find((t) => t.id === platz.typ)?.titel ?? platz.typ;
              const i = flach.findIndex((p) => p.id === platz.id);
              const bewege = (r: Richtung) => void aendern((l) => verschiebe(l, platz.id, r, fluss));
              return (
                <div key={platz.id} className={cn(bearbeiten && 'rounded-lg ring-1 ring-line-strong')}>
                  {bearbeiten && (
                    <div className="flex items-center gap-0.5 rounded-t-lg bg-surface-raised px-2 py-1">
                      <span className="beschriftung min-w-0 flex-1 truncate text-ink-faint">{titel}</span>
                      <button type="button" className={knopf} aria-label={`${titel} nach oben`} disabled={i === 0 || (!fluss && spalte[0] === platz)} onClick={() => bewege('hoch')}><ArrowUp className="size-3.5" /></button>
                      <button type="button" className={knopf} aria-label={`${titel} nach unten`} disabled={i === flach.length - 1 || (!fluss && spalte[spalte.length - 1] === platz)} onClick={() => bewege('runter')}><ArrowDown className="size-3.5" /></button>
                      {!fluss && (
                        <>
                          <button type="button" className={knopf} aria-label={`${titel} nach links`} disabled={s === 0} onClick={() => bewege('links')}><ArrowLeft className="size-3.5" /></button>
                          <button type="button" className={knopf} aria-label={`${titel} nach rechts`} disabled={s === 2} onClick={() => bewege('rechts')}><ArrowRight className="size-3.5" /></button>
                        </>
                      )}
                      <button type="button" className={knopf} aria-label={`${titel} entfernen`} onClick={() => { void aendern((l) => entferne(l, platz.id)); if (istZaehler) void zaehlerLoeschen(platz.id); if (istStatistik) void statistikLoeschen(platz.id); }}><X className="size-3.5" /></button>
                    </div>
                  )}
                  <div className={cn(bearbeiten && 'pointer-events-none select-none opacity-70')} inert={bearbeiten || undefined}>
                    {Komponente ? <Komponente /> : istStatistik ? <StatistikEigeneKachel id={platz.id} /> : <ZaehlerKachel id={platz.id} />}
                  </div>
                </div>
              );
            })}
          </PageColumn>
        ))}
      </PageGrid>

      <Dialog open={katalogOffen} onClose={() => setKatalogOffen(false)} title="Kachel hinzufügen">
        <ul className="flex flex-col gap-2">
          {frei.map((t) => (
            <li key={t.id} className="flex items-center gap-3 rounded-lg border border-line px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-bold">{t.titel}</p>
                <p className="text-[12px] text-ink-muted">{t.beschreibung}</p>
              </div>
              <Button size="sm" variant="primary" onClick={() => { void aendern((l) => fuegeHinzu(l, t.id, fest)); if (frei.length <= 1) setKatalogOffen(false); }}>Hinzufügen</Button>
            </li>
          ))}
          {frei.length === 0 && <li className="text-sm text-ink-faint">Alle Kacheln sind schon da.</li>}
        </ul>
        {areaId && (
          <form className="mt-3 flex flex-col gap-2 border-t border-line pt-3" onSubmit={(e) => {
            e.preventDefault();
            if (!zName.trim()) return;
            void zaehlerAnlegen(areaId, zName, Number(zZiel) > 0 ? Number(zZiel) : undefined).then((z) => aendern((l) => fuegePlatzHinzu(l, { id: z.id, typ: 'zaehler' })));
            setZName(''); setZZiel(''); setKatalogOffen(false);
          }}>
            <p className="text-[14px] font-bold">Eigener Zähler</p>
            <p className="text-[12px] text-ink-muted">Zum Hochzählen, z. B. „Bücher gelesen“ – optional mit Ziel und Fortschrittsbalken.</p>
            <div className="flex gap-2">
              <Input aria-label="Name des Zählers" placeholder="Name" value={zName} onChange={(e) => setZName(e.target.value)} />
              <Input aria-label="Ziel des Zählers" inputMode="numeric" placeholder="Ziel" className="w-20" value={zZiel} onChange={(e) => setZZiel(e.target.value)} />
              <Button type="submit" variant="primary" size="sm" disabled={!zName.trim()}>Anlegen</Button>
            </div>
          </form>
        )}
        {areaId && (
          <form className="mt-3 flex flex-col gap-2 border-t border-line pt-3" onSubmit={(e) => {
            e.preventDefault();
            void statistikAnlegen(areaId, sQuelle, sDarst).then((s) => aendern((l) => fuegePlatzHinzu(l, { id: s.id, typ: 'statistik' })));
            setKatalogOffen(false);
          }}>
            <p className="text-[14px] font-bold">Eigene Statistik</p>
            <p className="text-[12px] text-ink-muted">Wähle, was gezählt wird und wie es aussieht. Die Darstellung lässt sich später in der Kachel wechseln.</p>
            <div className="flex flex-wrap gap-2">
              <select aria-label="Datenquelle der neuen Statistik" value={sQuelle} onChange={(e) => setSQuelle(e.target.value as StatistikQuelle)} className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-ink">
                {(Object.keys(QUELLEN) as StatistikQuelle[]).map((k) => <option key={k} value={k}>{QUELLEN[k].label}</option>)}
              </select>
              <select aria-label="Darstellung der neuen Statistik" value={sDarst} onChange={(e) => setSDarst(e.target.value as StatistikDarstellung)} className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-ink">
                {(Object.keys(DARSTELLUNGEN) as StatistikDarstellung[]).map((k) => <option key={k} value={k}>{DARSTELLUNGEN[k]}</option>)}
              </select>
              <Button type="submit" variant="primary" size="sm">Anlegen</Button>
            </div>
          </form>
        )}
      </Dialog>
    </>
  );
}
